import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  BookOpen, Plus, Pencil, History, Trash2, GitCommitHorizontal, Search,
  ChevronLeft, Eye, Code2, RotateCcw, ShieldAlert, X, Tag, Send, Megaphone, Pin, Terminal,
  FileSignature, Receipt, ShieldCheck, ImagePlus, Sparkles, FileText, Loader2, Package,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { RestrictedBanner, SessionCountdown } from "@/components/PortalChrome";
import OnboardingTab from "@/components/devhub/OnboardingTab";
import EmailVerifyCard from "@/components/EmailVerifyCard";
import InvoicesTab from "@/components/devhub/InvoicesTab";
import ContractorTab from "@/components/devhub/ContractorTab";
import FabPacksTab from "@/components/devhub/FabPacksTab";

const WORK_QUOTES = [
  "Keep working. You're doing great.",
  "War never changes. Neither does the grind — back to it.",
  "Another day, another changeset. The wasteland thanks you.",
  "Productivity detected above acceptable levels. Keep it up, citizen.",
  "The vault doors don't rig themselves. Carry on.",
  "Morale status: EXCELLENT. Management is pleased with your output.",
  "Remember: every bug fixed today is a smoother Alpha tomorrow.",
  "You're not just writing code. You're building New Citizen.",
];

const EASE = [0.16, 1, 0.3, 1];
const CATEGORIES = [
  "Onboarding", "Blueprint", "C++ / Source", "Animation", "Level Design",
  "Networking / Multiplayer", "Build & Release", "Art Pipeline", "Audio", "QA / Testing",
];
const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase block mb-2";

const DOC_TEMPLATES = {
  "Onboarding": "# {Title}\n\n## Purpose\nWhy this doc exists and who it's for.\n\n## Prerequisites\n- Access needed\n- Software / plugins\n\n## Step-by-step\n1. First step\n2. Second step\n\n## Verification\nHow to confirm it worked.\n\n## Common pitfalls\n- Gotcha and its fix",
  "Blueprint": "# {Title}\n\n## Overview\nWhat this Blueprint system does in-game.\n\n## Classes & Parents\n- `BP_MyClass` — parent: `Actor`\n\n## Key functions\n| Function | Inputs | Outputs | Notes |\n|----------|--------|---------|-------|\n| `DoThing` | — | — | Server only |\n\n## Events & dispatchers\n- What fires what\n\n## Replication notes\n- What replicates and why\n\n## Debugging\n- Print strings / breakpoints to use",
  "C++ / Source": "# {Title}\n\n## Summary\nWhat this system/module does.\n\n## Classes\n| Class | Header | Responsibility |\n|-------|--------|----------------|\n| `UMyClass` | `MyClass.h` | … |\n\n## Key APIs\n```\nvoid UMyClass::DoThing(float DeltaTime);\n```\n\n## Build notes\n- Module dependencies (`Build.cs`)\n\n## Performance considerations\n- Tick usage, allocations",
  "Animation": "# {Title}\n\n## Scope\nWhich characters / skeletons this covers.\n\n## Assets\n- Anim BP: `ABP_X`\n- Blend spaces / state machines\n\n## Retargeting chain\nSource skeleton → IK Rig → target\n\n## Implementation notes\n- Slot names, montage setup\n\n## Known issues\n- Foot sliding on slopes etc.",
  "Level Design": "# {Title}\n\n## Layout intent\nFlow, sightlines, player goals.\n\n## Sub-levels & streaming\n- Which World Partition cells / sublevels\n\n## Lighting pass\n- Lumen settings, lightmass importance\n\n## Performance budget\n- Draw calls / tri counts target\n\n## Blockout → dressing checklist\n- [ ] Greybox approved\n- [ ] Meshing pass\n- [ ] Lighting pass\n- [ ] Optimization pass",
  "Networking / Multiplayer": "# {Title}\n\n## System overview\nWhat gameplay feature this networks.\n\n## Replication model\n| Property/RPC | Type | Condition | Why |\n|--------------|------|-----------|-----|\n\n## Bandwidth notes\n- Update frequencies, relevancy\n\n## Testing checklist\n- [ ] 2-client PIE\n- [ ] Dedicated server\n- [ ] Packet loss sim (`Net PktLag=150`)",
  "Build & Release": "# {Title}\n\n## Target\nPlatform + configuration (Shipping/Dev).\n\n## Command\n```\nBuildCookRun args here\n```\n\n## Versioning\n- How build numbers are set\n\n## Upload / distribution\n- Steam pipe / internal share\n\n## Troubleshooting\n- Error → fix table",
  "Art Pipeline": "# {Title}\n\n## Asset spec\n- Tri budget, texture sizes, texel density\n\n## Naming convention\n`SM_Category_Name_01`\n\n## Export settings\n- DCC → FBX settings\n\n## Import checklist\n- [ ] Collision\n- [ ] LODs\n- [ ] Materials assigned\n\n## Review criteria\nWhat leads check before approving",
  "Audio": "# {Title}\n\n## Scope\nSFX / music / VO covered.\n\n## Assets & cues\n- Sound classes, mixes\n\n## Implementation\n- Attenuation, concurrency\n\n## Mix notes\n- Bus routing",
  "QA / Testing": "# {Title}\n\n## Test objective\nWhat we're validating.\n\n## Environment\n- Build #, map, platform\n\n## Test cases\n| # | Steps | Expected | Result |\n|---|-------|----------|--------|\n| 1 | … | … | Pass/Fail |\n\n## Regression notes\n- Areas to re-test after fixes",
};

const MD_COMPONENTS = {
  h1: (p) => <h1 className="font-sans font-bold uppercase text-2xl text-zinc-50 mt-8 mb-4 first:mt-0" {...p} />,
  h2: (p) => <h2 className="font-sans font-bold uppercase text-lg text-zinc-100 mt-8 mb-3 border-b border-white/10 pb-2" {...p} />,
  h3: (p) => <h3 className="font-sans font-semibold uppercase text-base text-zinc-200 mt-6 mb-2" {...p} />,
  p: (p) => <p className="font-mono text-sm text-zinc-300 leading-relaxed mb-4" {...p} />,
  li: (p) => <li className="font-mono text-sm text-zinc-300 leading-relaxed ml-5 mb-1.5 list-disc" {...p} />,
  code: ({ className, children, ...p }) => {
    const isBlock = String(children).includes("\n") || (className || "").startsWith("language-");
    return isBlock
      ? <code className="block font-mono text-[13px] text-zinc-200 bg-black/60 border border-white/10 p-4 my-4 overflow-x-auto whitespace-pre" {...p}>{children}</code>
      : <code className="font-mono text-[13px] text-nc-orange bg-nc-orange/10 px-1.5 py-0.5" {...p}>{children}</code>;
  },
  pre: (p) => <pre className="my-4" {...p} />,
  a: (p) => <a className="text-nc-blue underline hover:text-nc-orange transition-colors" target="_blank" rel="noopener noreferrer" {...p} />,
  table: (p) => <table className="w-full border border-white/10 my-4 font-mono text-xs" {...p} />,
  th: (p) => <th className="border-b border-white/20 bg-white/5 px-3 py-2 text-left text-zinc-300 uppercase tracking-wider" {...p} />,
  td: (p) => <td className="border-b border-white/10 px-3 py-2 text-zinc-400" {...p} />,
  blockquote: (p) => <blockquote className="border-l-2 border-nc-blue pl-4 my-4 text-zinc-400 italic" {...p} />,
  img: ({ src, alt }) => (
    <span className="block my-5 border border-white/10 bg-black/40 p-2">
      <img src={src} alt={alt || "figure"} className="max-w-full h-auto mx-auto" loading="lazy" />
      {alt && <span className="block font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-600 text-center mt-2">{alt}</span>}
    </span>
  ),
};

function currentUser() {
  try { return JSON.parse(localStorage.getItem("nc_user")); } catch { return null; }
}

function CommsTab({ user, isAdmin }) {
  const [messages, setMessages] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [draft, setDraft] = useState("");
  const [annDraft, setAnnDraft] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const quote = WORK_QUOTES[Math.floor(Date.now() / 300000) % WORK_QUOTES.length];

  const load = useCallback(async () => {
    try {
      const [c, a] = await Promise.all([api.get("/dev/chat"), api.get("/dev/announcements")]);
      setMessages([...c.data].reverse());
      setAnnouncements(a.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      await api.post("/dev/chat", { message: draft.trim() });
      setDraft("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const postAnn = async () => {
    if (annDraft.trim().length < 3) return;
    setSending(true);
    try {
      await api.post("/dev/announcements", { message: annDraft.trim(), pinned: true });
      setAnnDraft("");
      load();
      toast.success("Announcement broadcast to the team");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Chat */}
      <div data-testid="dev-chat" className="lg:col-span-2 border border-white/10 bg-black/40 flex flex-col h-[560px]">
        <div className="border-b border-white/10 px-5 py-3 flex items-center justify-between">
          <span className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-nc-green animate-pulse" /> Team Channel — {messages.length} messages
          </span>
          <span data-testid="work-quote" className="font-mono text-[10px] text-nc-orange italic hidden sm:block">"{quote}"</span>
        </div>
        <div ref={listRef} data-testid="dev-chat-messages" data-lenis-prevent onWheel={(e) => e.stopPropagation()} className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
          {messages.length === 0 && <p className="font-mono text-xs text-zinc-600 text-center py-10">Quiet in here. Say something to the team.</p>}
          {messages.map((m, i) => {
            const mine = m.user_id === user?.id;
            return (
              <div key={m.id} data-testid={`chat-msg-${i + 1}`} className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`}>
                {m.avatar_path ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/profile/avatar/${m.user_id}`} alt="" className="w-8 h-8 rounded-full object-cover border border-white/15 shrink-0" />
                ) : (
                  <span className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 font-sans font-bold text-[11px] ${m.user_role === "admin" ? "border-nc-orange/40 bg-nc-orange/10 text-nc-orange" : "border-nc-blue/40 bg-nc-blue/10 text-nc-blue"}`}>
                    {(m.user_name || "?").slice(0, 1).toUpperCase()}
                  </span>
                )}
                <div className={`max-w-[75%] border px-3.5 py-2.5 ${mine ? "border-nc-blue/40 bg-nc-blue/10" : "border-white/10 bg-[#050505]/70"}`}>
                  <p className="font-mono text-[9px] tracking-[0.15em] uppercase mb-1">
                    <span className={m.user_role === "admin" ? "text-nc-orange" : "text-nc-blue"}>{m.user_name}</span>
                    <span className="text-zinc-600"> // {String(m.at).slice(5, 16).replace("T", " ")}</span>
                  </p>
                  <p className="font-mono text-xs text-zinc-200 leading-relaxed break-words">{m.message}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="border-t border-white/10 p-3 flex gap-2">
          <input
            data-testid="dev-chat-input"
            className="flex-1 bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue"
            placeholder="Message the team…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !sending) send(); }}
            maxLength={1000}
          />
          <button data-testid="dev-chat-send" onClick={send} disabled={sending || !draft.trim()} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white px-5 flex items-center transition-colors">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Announcements */}
      <div data-testid="dev-announcements" className="border border-white/10 bg-black/40 flex flex-col h-[560px]">
        <div className="border-b border-white/10 px-5 py-3">
          <span className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2">
            <Megaphone className="w-3.5 h-3.5" /> Announcements
          </span>
        </div>
        <div data-testid="dev-announcements-list" data-lenis-prevent onWheel={(e) => e.stopPropagation()} className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
          {announcements.length === 0 && <p className="font-mono text-xs text-zinc-600 text-center py-10">No announcements.</p>}
          {announcements.map((a, i) => (
            <div key={a.id} data-testid={`announcement-${i + 1}`} className="border border-nc-orange/30 bg-nc-orange/5 p-4 relative">
              {a.pinned && <Pin className="w-3 h-3 text-nc-orange absolute top-3 right-3" />}
              <p className="font-mono text-xs text-zinc-200 leading-relaxed pr-5">{a.message}</p>
              <p className="font-mono text-[9px] text-zinc-600 mt-2">{a.author} // {String(a.at).slice(0, 10)}</p>
              {isAdmin && (
                <button data-testid={`announcement-delete-${i + 1}`} onClick={async () => { await api.delete(`/dev/announcements/${a.id}`); load(); }} className="font-mono text-[9px] text-red-400 hover:text-red-300 uppercase mt-2">Remove</button>
              )}
            </div>
          ))}
        </div>
        {isAdmin && (
          <div className="border-t border-white/10 p-3 flex flex-col gap-2">
            <textarea
              data-testid="announcement-input"
              rows={2}
              className="w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-xs text-zinc-100 rounded-none resize-none focus:outline-none focus:ring-1 focus:ring-nc-orange"
              placeholder="Broadcast to the team…"
              value={annDraft}
              onChange={(e) => setAnnDraft(e.target.value)}
              maxLength={1000}
            />
            <button data-testid="announcement-send" onClick={postAnn} disabled={sending || annDraft.trim().length < 3} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-2.5 transition-colors">
              Post Announcement
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function MotdModal({ announcements, onClose }) {
  const quote = WORK_QUOTES[Math.floor(Date.now() / 300000) % WORK_QUOTES.length];
  const latest = announcements[0];
  return (
    <motion.div data-testid="motd-modal" className="fixed inset-0 z-[95] bg-black/85 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.div className="border border-nc-orange/40 bg-[#0a0a0a] max-w-lg w-full" initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}>
        <div className="bg-nc-orange px-5 py-2.5 flex items-center gap-2">
          <Terminal className="w-4 h-4 text-white" />
          <span className="font-mono text-[11px] tracking-[0.3em] uppercase font-bold text-white">Message of the Day</span>
        </div>
        <div className="p-6 flex flex-col gap-4">
          {latest ? (
            <div>
              <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase mb-2">Studio broadcast — {latest.author} // {String(latest.at).slice(0, 10)}</p>
              <p data-testid="motd-message" className="font-mono text-sm text-zinc-100 leading-relaxed">{latest.message}</p>
            </div>
          ) : (
            <p className="font-mono text-sm text-zinc-400 leading-relaxed">No studio broadcast today. Check the Announcements panel for updates.</p>
          )}
          <div className="border-t border-white/10 pt-4">
            <p data-testid="motd-quote" className="font-mono text-xs text-nc-green italic">"{quote}"</p>
          </div>
          <div className="border border-red-500/30 bg-red-500/5 px-4 py-3">
            <p className="font-mono text-[9px] tracking-[0.15em] text-red-300 uppercase leading-relaxed">
              Reminder: NDA in force. Everything in this hub is confidential studio property.
            </p>
          </div>
          <button data-testid="motd-dismiss" onClick={onClose} className="bg-nc-blue hover:bg-nc-blue/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-3 transition-colors self-start">
            Understood — to work
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Editor({ initial, onSave, onCancel, saving, teams = [] }) {
  const [form, setForm] = useState(initial);
  const [preview, setPreview] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [imgBusy, setImgBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [gdocUrl, setGdocUrl] = useState("");
  const contentRef = useRef(null);
  const imgInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !form.tags.includes(t)) setForm((f) => ({ ...f, tags: [...f.tags, t] }));
    setTagInput("");
  };

  const insertAtCursor = (snippet) => {
    const el = contentRef.current;
    if (!el) { setForm((f) => ({ ...f, content: f.content + "\n\n" + snippet })); return; }
    const pos = el.selectionStart ?? form.content.length;
    setForm((f) => ({ ...f, content: f.content.slice(0, pos) + "\n" + snippet + "\n" + f.content.slice(pos) }));
  };

  const uploadImage = async (file) => {
    if (!file || imgBusy) return;
    setImgBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/dev/docs/images", fd);
      insertAtCursor(r.data.markdown);
      toast.success("Image embedded at cursor");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Image upload failed");
    } finally {
      setImgBusy(false);
      if (imgInputRef.current) imgInputRef.current.value = "";
    }
  };

  const runImport = async (pdfFile) => {
    if (importBusy) return;
    if (!pdfFile && !gdocUrl.trim()) { toast.error("Attach a PDF or paste a Google Docs link"); return; }
    setImportBusy(true);
    try {
      const fd = new FormData();
      if (pdfFile) fd.append("file", pdfFile);
      else fd.append("gdoc_url", gdocUrl.trim());
      const r = await api.post("/dev/docs/import", fd);
      setForm((f) => ({ ...f, title: f.title || r.data.title, content: r.data.content }));
      setImportOpen(false);
      toast.success(`Imported from ${r.data.source}${r.data.images ? ` — ${r.data.images} image${r.data.images === 1 ? "" : "s"} embedded` : ""}. Review and save.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImportBusy(false);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Title *</label>
          <input data-testid="doc-title-input" className={inputCls} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. UE5 Dedicated Server Setup" />
        </div>
        <div>
          <label className={labelCls}>Category *</label>
          <select
            data-testid="doc-category-select"
            className={inputCls}
            value={form.category}
            onChange={(e) => {
              const cat = e.target.value;
              setForm((f) => ({
                ...f,
                category: cat,
                content: !f.id && (!f.content || Object.values(DOC_TEMPLATES).includes(f.content))
                  ? (DOC_TEMPLATES[cat] || "")
                  : f.content,
              }));
            }}
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {!initial.id && <p className="font-mono text-[9px] text-zinc-600 mt-1.5">Template auto-fills per category — just replace the guidance.</p>}
        </div>
      </div>

      <div>
        <label className={labelCls}>Visibility (team)</label>
        <select data-testid="doc-team-select" className={inputCls} value={form.team_id || ""} onChange={(e) => setForm((f) => ({ ...f, team_id: e.target.value }))}>
          <option value="">Global — visible to all devs</option>
          {teams.map((t) => <option key={t.id} value={t.id} children={`${t.name} — members only`} />)}
        </select>
        <p className="font-mono text-[9px] text-zinc-600 mt-1.5">Team documents are hidden from devs outside that team.</p>
      </div>

      <div>
        <label className={labelCls}>Tags</label>
        <div className="flex gap-2">
          <input data-testid="doc-tag-input" className={inputCls} value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} placeholder="Type a tag, press Enter" />
          <button type="button" data-testid="doc-add-tag-button" onClick={addTag} className="border border-white/10 px-4 text-zinc-400 hover:border-nc-blue hover:text-nc-blue transition-colors"><Tag className="w-4 h-4" /></button>
        </div>
        {form.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {form.tags.map((t) => (
              <span key={t} className="font-mono text-[10px] text-nc-blue border border-nc-blue/40 bg-nc-blue/10 px-2 py-1 flex items-center gap-1.5">
                {t}
                <button type="button" onClick={() => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }))} className="hover:text-white"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <label className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">Content (Markdown) *</label>
          <div className="flex items-center gap-2">
            <button type="button" data-testid="doc-image-upload-button" onClick={() => imgInputRef.current?.click()} disabled={imgBusy}
                    className="font-mono text-[10px] tracking-[0.2em] uppercase flex items-center gap-2 text-zinc-400 hover:text-nc-blue transition-colors disabled:opacity-40">
              <ImagePlus className="w-3.5 h-3.5" /> {imgBusy ? "Uploading…" : "Image"}
            </button>
            <input ref={imgInputRef} data-testid="doc-image-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                   onChange={(e) => uploadImage(e.target.files?.[0])} />
            <button type="button" data-testid="doc-ai-import-button" onClick={() => setImportOpen((o) => !o)}
                    className="font-mono text-[10px] tracking-[0.2em] uppercase flex items-center gap-2 text-zinc-400 hover:text-nc-orange transition-colors">
              <Sparkles className="w-3.5 h-3.5" /> AI Import
            </button>
            <button type="button" data-testid="doc-preview-toggle" onClick={() => setPreview((p) => !p)} className="font-mono text-[10px] tracking-[0.2em] uppercase flex items-center gap-2 text-zinc-400 hover:text-nc-blue transition-colors">
              {preview ? <Code2 className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {preview ? "Write" : "Preview"}
            </button>
          </div>
        </div>
        {importOpen && (
          <div data-testid="doc-import-panel" className="border border-nc-orange/30 bg-nc-orange/5 p-4 mb-3 flex flex-col gap-3">
            <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> AI Import — PDF or Google Doc → documentation
            </p>
            <p className="font-mono text-[10px] text-zinc-500 leading-relaxed">
              Drop a PDF and AI converts the text AND embeds its images. Or paste a Google Docs link shared as "Anyone with the link can view" (text only — use PDF if you need the images).
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <input ref={pdfInputRef} data-testid="doc-import-pdf-input" type="file" accept="application/pdf" className="hidden"
                     onChange={(e) => e.target.files?.[0] && runImport(e.target.files[0])} />
              <button type="button" data-testid="doc-import-pdf-button" onClick={() => pdfInputRef.current?.click()} disabled={importBusy}
                      className="font-mono text-[10px] uppercase tracking-[0.15em] border border-white/20 hover:border-nc-orange text-zinc-200 px-4 py-2 transition-colors disabled:opacity-40 flex items-center gap-2">
                <FileText className="w-3.5 h-3.5" /> Choose PDF
              </button>
              <span className="font-mono text-[9px] text-zinc-600 uppercase">or</span>
              <input data-testid="doc-import-gdoc-input" value={gdocUrl} onChange={(e) => setGdocUrl(e.target.value)}
                     placeholder="https://docs.google.com/document/d/…" className={`${inputCls} flex-1 min-w-[220px]`} />
              <button type="button" data-testid="doc-import-run" onClick={() => runImport(null)} disabled={importBusy || !gdocUrl.trim()}
                      className="font-mono text-[10px] uppercase tracking-[0.15em] bg-nc-orange hover:bg-nc-orange/80 text-black px-4 py-2 transition-colors disabled:opacity-40">
                {importBusy ? "Converting…" : "Convert"}
              </button>
            </div>
            {importBusy && <p className="font-mono text-[9px] text-nc-orange animate-pulse">AI is reading and structuring your document — this can take 30–60s…</p>}
          </div>
        )}
        {preview ? (
          <div data-testid="doc-markdown-preview" data-lenis-prevent onWheel={(e) => e.stopPropagation()} className="border border-white/10 bg-black/40 p-6 min-h-[320px] max-h-[70vh] overflow-y-auto">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{form.content || "*Nothing to preview yet*"}</ReactMarkdown>
          </div>
        ) : (
          <textarea ref={contentRef} data-testid="doc-content-input" data-lenis-prevent onWheel={(e) => e.stopPropagation()} rows={16} className={`${inputCls} resize-y leading-relaxed`} value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} placeholder={"# Heading\n\n## Section\n\n- point\n\n`code`\n\n![figure](upload with the Image button)"} />
        )}
      </div>

      {initial.id && (
        <div>
          <label className={labelCls}>Edit summary</label>
          <input data-testid="doc-note-input" className={inputCls} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="What changed in this edit?" />
        </div>
      )}

      <div className="flex gap-3 justify-end">
        <button type="button" data-testid="doc-cancel-button" onClick={onCancel} className="font-mono text-[10px] tracking-[0.25em] uppercase px-5 py-3 border border-white/10 text-zinc-400 hover:text-zinc-100 transition-colors">Cancel</button>
        <button type="button" data-testid="doc-save-button" onClick={() => onSave(form)} disabled={saving || form.title.trim().length < 3 || form.content.trim().length === 0} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-3 transition-colors">
          {saving ? "Saving..." : initial.id ? "Save New Version" : "Publish Document"}
        </button>
      </div>
    </div>
  );
}

function HistoryPanel({ docId, onClose, onRestore }) {
  const [versions, setVersions] = useState([]);
  const [peek, setPeek] = useState(null);

  useEffect(() => {
    api.get(`/dev/docs/${docId}/versions`).then((r) => setVersions(r.data)).catch(() => {});
  }, [docId]);

  return (
    <motion.div data-testid="history-panel" className="fixed inset-y-0 right-0 z-[90] w-full max-w-md bg-[#0a0a0a] border-l border-white/15 flex flex-col" initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ duration: 0.35, ease: EASE }}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <span className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-2"><History className="w-4 h-4" /> Version History</span>
        <button data-testid="history-close-button" onClick={onClose} className="text-zinc-500 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
      </div>
      <div data-testid="history-version-list" data-lenis-prevent onWheel={(e) => e.stopPropagation()} className="flex-1 overflow-y-auto p-4 flex flex-col gap-2">
        {versions.map((v) => (
          <div key={v.id} data-testid={`history-version-${v.version}`} className="border border-white/10 bg-black/40 p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="font-sans font-bold text-sm text-zinc-100">v{v.version}</span>
              <span className="font-mono text-[9px] text-zinc-600">{String(v.created_at).slice(0, 16).replace("T", " ")}</span>
            </div>
            <p className="font-mono text-xs text-zinc-400 mt-1.5">{v.note || "—"}</p>
            <p className="font-mono text-[10px] text-zinc-600 mt-1">by {v.editor_name}</p>
            <div className="flex gap-2 mt-3">
              <button data-testid={`history-peek-${v.version}`} onClick={() => setPeek(peek === v.version ? null : v.version)} className="font-mono text-[9px] tracking-[0.2em] uppercase px-3 py-1.5 border border-white/10 text-zinc-400 hover:border-white/30 transition-colors">
                {peek === v.version ? "Hide" : "View"}
              </button>
              <button data-testid={`history-restore-${v.version}`} onClick={() => onRestore(v.version)} className="font-mono text-[9px] tracking-[0.2em] uppercase px-3 py-1.5 border border-nc-orange/40 text-nc-orange hover:bg-nc-orange/10 transition-colors flex items-center gap-1.5">
                <RotateCcw className="w-3 h-3" /> Restore
              </button>
            </div>
            {peek === v.version && <VersionPeek docId={docId} version={v.version} />}
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function VersionPeek({ docId, version }) {
  const [content, setContent] = useState(null);
  useEffect(() => {
    api.get(`/dev/docs/${docId}/versions/${version}`).then((r) => setContent(r.data.content)).catch(() => {});
  }, [docId, version]);
  if (content == null) return <p className="font-mono text-xs text-zinc-600 mt-3">Loading…</p>;
  return (
    <div data-testid={`history-peek-content-${version}`} data-lenis-prevent onWheel={(e) => e.stopPropagation()} className="mt-3 border-t border-white/10 pt-3 max-h-64 overflow-y-auto">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{content}</ReactMarkdown>
    </div>
  );
}

export default function DevHubPage() {
  const navigate = useNavigate();
  const [freshUser, setFreshUser] = useState(null);
  const [needsVerify, setNeedsVerify] = useState(false);
  const user = freshUser || currentUser();
  const isAdmin = user?.role === "admin";
  const isDev = user?.role === "dev" || isAdmin;

  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState("read"); // read | edit | new
  const [tab, setTab] = useState("docs"); // docs | comms | plastic | onboarding | invoices
  const [fabAllowed, setFabAllowed] = useState(false);
  useEffect(() => { api.get("/dev/fab/access").then((r) => setFabAllowed(Boolean(r.data?.allowed))).catch(() => {}); }, []);
  const [search, setSearch] = useState("");
  const [aiQuery, setAiQuery] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [catFilter, setCatFilter] = useState("all");
  const [teams, setTeams] = useState([]);
  const [teamFilter, setTeamFilter] = useState("all"); // all | global | team_id
  const [saving, setSaving] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [removalFor, setRemovalFor] = useState(null);
  const [removalReason, setRemovalReason] = useState("");
  const [checkins, setCheckins] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [motdDismissed, setMotdDismissed] = useState(false);
  const [onboarded, setOnboarded] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/dev/docs");
      setDocs(r.data);
      return r.data;
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (err.response?.status === 403 && typeof detail === "string" && detail.includes("Verify your email")) {
        setNeedsVerify(true);
      } else if (err.response?.status === 403 || err.response?.status === 401) {
        toast.error("Dev access required — sign in with a dev account");
        navigate("/auth");
      }
      return [];
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    // Refresh the stored user — role grants (invites, admin changes) land without a re-login
    api.get("/auth/me").then((r) => {
      localStorage.setItem("nc_user", JSON.stringify(r.data));
      setFreshUser(r.data);
    }).catch(() => setFreshUser(false));
  }, []);

  useEffect(() => {
    if (freshUser === null) return; // wait for the fresh role check before gating
    if (!isDev) { navigate("/auth"); return; }
    load();
    api.get("/dev/teams").then((r) => setTeams(r.data)).catch(() => {});
    api.get("/dev/plastic/checkins").then((r) => setCheckins(r.data.webhookEvents || [])).catch(() => {});
    api.get("/dev/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
    api.get("/contracts/mine").then(async (r) => {
      const types = new Set(r.data.signed.map((d) => d.doc_type));
      const idStatus = await api.get("/onboarding/identity/status").catch(() => null);
      setOnboarded(types.has("nda") && types.has("ip") && idStatus?.data?.id_status === "verified");
    }).catch(() => {});
  }, [isDev, load, navigate, freshUser]);

  const filtered = useMemo(() => docs.filter((d) =>
    (catFilter === "all" || d.category === catFilter) &&
    (teamFilter === "all" || (teamFilter === "global" ? !d.team_id : d.team_id === teamFilter)) &&
    (!search || d.title.toLowerCase().includes(search.toLowerCase()) || (d.tags || []).some((t) => t.includes(search.toLowerCase())))
  ), [docs, catFilter, teamFilter, search]);

  const runAiSearch = async () => {
    const q = aiQuery.trim();
    if (q.length < 3) return toast.error("Ask at least a three-character question");
    setAiBusy(true);
    try {
      const r = await api.post("/dev/docs/search", { query: q, limit: 6 });
      setAiResult(r.data);
      if (!r.data.results?.length) toast.info("No matching docs found");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI search failed");
    } finally { setAiBusy(false); }
  };


  const openDoc = async (id) => {
    try {
      const r = await api.get(`/dev/docs/${id}`);
      setSelected(r.data);
      setMode("read");
    } catch { toast.error("Could not load document"); }
  };

  const saveDoc = async (form) => {
    setSaving(true);
    try {
      if (form.id) {
        const r = await api.put(`/dev/docs/${form.id}`, form);
        setSelected(r.data);
        toast.success(`Saved as v${r.data.version}`);
      } else {
        const r = await api.post("/dev/docs", form);
        setSelected(r.data);
        toast.success("Document published");
      }
      setMode("read");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const restoreVersion = async (version) => {
    try {
      await api.post(`/dev/docs/${historyFor}/restore/${version}`);
      toast.success(`Restored v${version}`);
      setHistoryFor(null);
      await openDoc(historyFor);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const requestRemoval = async () => {
    try {
      await api.post(`/dev/docs/${removalFor}/removal-request`, { reason: removalReason });
      toast.success("Removal requested — admin must approve");
      setRemovalFor(null);
      setRemovalReason("");
      if (selected?.id === removalFor) setSelected((s) => ({ ...s, status: "pending_removal" }));
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const adminDelete = async (id) => {
    try {
      await api.delete(`/dev/docs/${id}`);
      toast.success("Document permanently deleted");
      setRemovalFor(null);
      if (selected?.id === id) setSelected(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!isDev) return null;

  if (needsVerify) {
    return (
      <div data-testid="devhub-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
        <RestrictedBanner area="DEV TEAM" />
        <SiteNav />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-lg flex flex-col gap-4">
            <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase text-center">Dev Hub // Security Gate</p>
            <EmailVerifyCard email={user?.email || ""} testid="devhub-email-verify" onVerified={() => window.location.reload()} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="devhub-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <RestrictedBanner area="DEV TEAM" />
      <SiteNav />

      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-14">
          <p className="font-mono text-xs tracking-[0.4em] text-nc-orange uppercase mb-3">Internal // New Citizen Dev Team</p>
          <h1 data-testid="devhub-title" className="font-sans font-bold uppercase tracking-tight text-2xl sm:text-4xl text-zinc-50">Dev Hub</h1>
          <p className="font-mono text-xs text-zinc-500 mt-3 max-w-xl leading-relaxed">
            Official internal documentation for the Unreal Engine 5 team. Every edit is versioned — nothing is ever lost, and only admins can permanently delete.
          </p>
          <div className="flex items-center justify-between mt-6 gap-4 flex-wrap">
          <div className="flex gap-2">
            <button data-testid="devhub-tab-docs" onClick={() => setTab("docs")} className={`font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2 border transition-colors ${tab === "docs" ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>
              <BookOpen className="w-3.5 h-3.5 inline mr-2" />Documentation ({docs.length})
            </button>
            <button data-testid="devhub-tab-comms" onClick={() => setTab("comms")} className={`font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2 border transition-colors ${tab === "comms" ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>
              <Megaphone className="w-3.5 h-3.5 inline mr-2" />Comms
            </button>
            <button data-testid="devhub-tab-onboarding" onClick={() => setTab("onboarding")} className={`font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2 border transition-colors ${tab === "onboarding" ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>
              <FileSignature className="w-3.5 h-3.5 inline mr-2" />Onboarding
            </button>
            <button data-testid="devhub-tab-invoices" onClick={() => {
              setTab("invoices");
              api.get("/contracts/mine").then(async (r) => {
                const types = new Set(r.data.signed.map((d) => d.doc_type));
                const idStatus = await api.get("/onboarding/identity/status").catch(() => null);
                setOnboarded(types.has("nda") && types.has("ip") && idStatus?.data?.id_status === "verified");
              }).catch(() => {});
            }} className={`font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2 border transition-colors ${tab === "invoices" ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>
              <Receipt className="w-3.5 h-3.5 inline mr-2" />Invoices
            </button>
            <button data-testid="devhub-tab-plastic" onClick={() => setTab("plastic")} className={`font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2 border transition-colors ${tab === "plastic" ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>
              <GitCommitHorizontal className="w-3.5 h-3.5 inline mr-2" />Plastic Feed ({checkins.length})
            </button>
            <button data-testid="devhub-tab-contractor" onClick={() => setTab("contractor")} className={`font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2 border transition-colors ${tab === "contractor" ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>
              <ShieldCheck className="w-3.5 h-3.5 inline mr-2" />Contractor
            </button>
            {fabAllowed && (
            <button data-testid="devhub-tab-fabpacks" onClick={() => setTab("fabpacks")} className={`font-mono text-[10px] tracking-[0.25em] uppercase px-4 py-2 border transition-colors ${tab === "fabpacks" ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>
              <Package className="w-3.5 h-3.5 inline mr-2" />Fab Packs
            </button>
            )}
          </div>
          <SessionCountdown />
          </div>
        </div>
      </header>

      {tab === "onboarding" ? (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 w-full">
          <OnboardingTab user={user} onNavigateInvoices={() => setTab("invoices")} />
        </main>
      ) : tab === "contractor" ? (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 w-full">
          <ContractorTab />
        </main>
      ) : tab === "fabpacks" ? (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 w-full">
          <FabPacksTab />
        </main>
      ) : tab === "invoices" ? (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 w-full">
          <InvoicesTab onboarded={onboarded} />
        </main>
      ) : tab === "comms" ? (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 w-full">
          <CommsTab user={user} isAdmin={isAdmin} />
        </main>
      ) : tab === "plastic" ? (
        <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 w-full">
          <div data-testid="plastic-feed" className="flex flex-col gap-3">
            {checkins.length === 0 && (
              <div className="border border-white/10 bg-black/40 p-8 md:p-10">
                <GitCommitHorizontal className="w-8 h-8 text-zinc-600 mx-auto mb-4" />
                <p className="font-mono text-xs text-zinc-400 leading-relaxed max-w-lg mx-auto text-center">
                  No checkins received yet. Connect Unity Cloud once and every pull & submit lands here live.
                </p>
                <div className="mt-6 border border-nc-blue/30 bg-nc-blue/5 p-5 text-left max-w-xl mx-auto">
                  <p className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase mb-3">One-time setup — Unity Cloud</p>
                  <ol className="font-mono text-[11px] text-zinc-400 leading-relaxed flex flex-col gap-2 list-decimal list-inside">
                    <li>Open Unity Cloud → DevOps → Version Control → Settings → Integrations</li>
                    <li>New integration → Webhook, repository: <span className="text-zinc-200">New Citizen</span></li>
                    <li>Event: <span className="text-zinc-200">after checkin / changeset created</span></li>
                    <li>Paste the Payload URL below</li>
                  </ol>
                  <div className="mt-4 bg-black/60 border border-white/10 p-3 break-all">
                    <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-600 uppercase mb-1">Payload URL</p>
                    <code data-testid="plastic-webhook-url" className="font-mono text-[11px] text-nc-green select-all">
                      {process.env.REACT_APP_BACKEND_URL}/api/dev/plastic/webhook?secret=sp-plastic-wh-7f3a9c2e1d4b8f60
                    </code>
                  </div>
                  <p className="font-mono text-[10px] text-zinc-600 mt-3 leading-relaxed">
                    Make any test checkin afterwards — it appears here within seconds.
                  </p>
                </div>
              </div>
            )}
            {checkins.map((c, i) => (
              <motion.div key={c.event_key || i} data-testid={`plastic-checkin-${i + 1}`} className="border border-white/10 bg-black/40 p-5 flex items-start gap-4" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05, ease: EASE }}>
                <span className="w-9 h-9 border border-nc-orange/40 bg-nc-orange/10 flex items-center justify-center shrink-0">
                  <GitCommitHorizontal className="w-4 h-4 text-nc-orange" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-sm text-zinc-100 break-words">{c.comment || "(no comment)"}</p>
                  <p className="font-mono text-[10px] text-zinc-500 mt-1.5">
                    {c.user || "unknown"} // {c.changeset || "changeset"} {c.branch ? `// ${c.branch}` : ""}
                  </p>
                </div>
                <span className="font-mono text-[9px] text-zinc-600 shrink-0">{String(c.received_at).slice(0, 16).replace("T", " ")}</span>
              </motion.div>
            ))}
          </div>
        </main>
      ) : (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 w-full grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* AI documentation search */}
          <div data-testid="doc-ai-search-panel" className="lg:col-span-4 border border-nc-blue/30 bg-[#061019]/75 p-4 md:p-5">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="shrink-0">
                <p className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5" /> Ask the documentation
                </p>
                <p className="font-mono text-[10px] text-zinc-600 mt-1">Permission-aware search across every system, pipeline, and runbook you can access.</p>
              </div>
              <div className="flex-1 flex gap-2">
                <input
                  data-testid="doc-ai-search-input"
                  className={inputCls}
                  placeholder="Ask: how do I wire a new CEF overlay?"
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !aiBusy) runAiSearch(); }}
                  maxLength={500}
                />
                <button data-testid="doc-ai-search-button" onClick={runAiSearch} disabled={aiBusy || aiQuery.trim().length < 3}
                  className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-mono text-[10px] tracking-[0.2em] uppercase px-5 flex items-center gap-2 transition-colors">
                  {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
                </button>
              </div>
            </div>
            {aiResult && (
              <div data-testid="doc-ai-search-result" className="mt-4 border-t border-white/10 pt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase mb-2">
                    {aiResult.ai ? "AI answer" : "Search guidance"} // {aiResult.searched} candidate{aiResult.searched === 1 ? "" : "s"}
                  </p>
                  <p className="font-mono text-xs text-zinc-200 leading-relaxed whitespace-pre-wrap">{aiResult.answer}</p>
                </div>
                <div data-testid="doc-ai-search-sources" className="flex flex-col gap-2">
                  <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase">Sources</p>
                  {(aiResult.sources || []).map((s) => (
                    <button key={s.id} data-testid={`doc-ai-source-${s.id}`} onClick={() => openDoc(s.id)}
                      className="text-left border border-white/10 hover:border-nc-blue/60 bg-black/30 px-3 py-2 transition-colors">
                      <p className="font-sans font-semibold text-xs text-zinc-100">{s.title}</p>
                      <p className="font-mono text-[9px] text-zinc-600 mt-1">{s.category}</p>
                    </button>
                  ))}
                  {(aiResult.sources || []).length === 0 && <p className="font-mono text-[10px] text-zinc-600">No sources to cite.</p>}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="flex flex-col gap-4">
            <button data-testid="devhub-new-doc-button" onClick={() => { setSelected(null); setMode("new"); }} className="bg-nc-blue hover:bg-nc-blue/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-4 py-3 flex items-center justify-center gap-2 transition-colors">
              <Plus className="w-4 h-4" /> New Document
            </button>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-zinc-600 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="devhub-search-input" className={`${inputCls} pl-9`} placeholder="Search title or tag…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <select data-testid="devhub-category-filter" className={inputCls} value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
              <option value="all">All categories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="devhub-team-filter" className={inputCls} value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)}>
              <option value="all">All teams</option>
              <option value="global">Global (all devs)</option>
              {teams.map((t) => <option key={t.id} value={t.id} children={t.name} />)}
            </select>
            <div data-testid="devhub-doc-list" data-lenis-prevent onWheel={(e) => e.stopPropagation()} className="flex flex-col gap-1.5 max-h-[480px] overflow-y-auto pr-1">
              {filtered.map((d) => (
                <button
                  key={d.id}
                  data-testid={`devhub-doc-${d.id}`}
                  onClick={() => openDoc(d.id)}
                  className={`text-left border px-3 py-2.5 transition-colors duration-200 ${selected?.id === d.id ? "border-nc-blue bg-nc-blue/10" : "border-white/10 hover:border-white/30"}`}
                >
                  <p className="font-sans font-semibold text-xs text-zinc-100 leading-snug">{d.title}</p>
                  <p className="font-mono text-[9px] text-zinc-600 mt-1">
                    {d.category} // v{d.version} // {d.author_name}
                    {d.team_id && <span data-testid={`devhub-doc-team-${d.id}`} className="text-nc-green ml-1">// {(teams.find((t) => t.id === d.team_id) || {}).name || "team"}</span>}
                    {d.status === "pending_removal" && <span className="text-nc-orange ml-1">// REMOVAL PENDING</span>}
                  </p>
                </button>
              ))}
              {!loading && filtered.length === 0 && <p className="font-mono text-xs text-zinc-600 p-3">No documents match.</p>}
            </div>
          </aside>

          {/* Main pane */}
          <section className="lg:col-span-3">
            {mode === "new" ? (
              <div data-testid="devhub-editor-new" className="border border-white/10 bg-black/40 p-6 md:p-8">
                <h2 className="font-sans font-bold uppercase text-lg text-zinc-50 mb-6">New Document</h2>
                <Editor initial={{ title: "", category: CATEGORIES[0], tags: [], content: DOC_TEMPLATES[CATEGORIES[0]], note: "", team_id: "" }} onSave={saveDoc} onCancel={() => setMode("read")} saving={saving} teams={teams} />
              </div>
            ) : mode === "edit" && selected ? (
              <div data-testid="devhub-editor-edit" className="border border-white/10 bg-black/40 p-6 md:p-8">
                <h2 className="font-sans font-bold uppercase text-lg text-zinc-50 mb-6">Editing — v{selected.version} → v{selected.version + 1}</h2>
                <Editor initial={{ id: selected.id, title: selected.title, category: selected.category, tags: selected.tags || [], content: selected.content, note: "", team_id: selected.team_id || "" }} onSave={saveDoc} onCancel={() => setMode("read")} saving={saving} teams={teams} />
              </div>
            ) : selected ? (
              <motion.article key={selected.id} data-testid="devhub-doc-view" className="border border-white/10 bg-black/40" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}>
                <div className="border-b border-white/10 px-6 md:px-8 py-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="font-mono text-[9px] tracking-[0.3em] text-nc-orange uppercase mb-2">{selected.category} // v{selected.version}</p>
                      <h2 data-testid="devhub-doc-title" className="font-sans font-bold uppercase text-xl md:text-2xl text-zinc-50">{selected.title}</h2>
                      <p className="font-mono text-[10px] text-zinc-600 mt-2">
                        by {selected.author_name} // updated {String(selected.updated_at).slice(0, 16).replace("T", " ")}
                      </p>
                      {(selected.tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {selected.tags.map((t) => <span key={t} className="font-mono text-[9px] text-nc-blue border border-nc-blue/40 bg-nc-blue/10 px-2 py-0.5">{t}</span>)}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button data-testid="doc-edit-button" onClick={() => setMode("edit")} className="w-9 h-9 border border-white/10 flex items-center justify-center text-zinc-400 hover:text-nc-blue hover:border-nc-blue/60 transition-colors" title="Edit"><Pencil className="w-4 h-4" /></button>
                      <button data-testid="doc-history-button" onClick={() => setHistoryFor(selected.id)} className="w-9 h-9 border border-white/10 flex items-center justify-center text-zinc-400 hover:text-nc-blue hover:border-nc-blue/60 transition-colors" title="Version history"><History className="w-4 h-4" /></button>
                      {isAdmin ? (
                        <button data-testid="doc-delete-button" onClick={() => setRemovalFor(selected.id)} className="w-9 h-9 border border-white/10 flex items-center justify-center text-zinc-400 hover:text-red-400 hover:border-red-400/60 transition-colors" title="Delete (admin)"><Trash2 className="w-4 h-4" /></button>
                      ) : (
                        <button data-testid="doc-removal-button" onClick={() => setRemovalFor(selected.id)} className="w-9 h-9 border border-white/10 flex items-center justify-center text-zinc-400 hover:text-nc-orange hover:border-nc-orange/60 transition-colors" title="Request removal"><ShieldAlert className="w-4 h-4" /></button>
                      )}
                    </div>
                  </div>
                  {selected.status === "pending_removal" && (
                    <div data-testid="doc-removal-banner" className="mt-4 border border-nc-orange/40 bg-nc-orange/10 px-4 py-2.5 font-mono text-[10px] text-nc-orange uppercase tracking-[0.2em]">
                      Removal requested — waiting for admin approval
                    </div>
                  )}
                </div>
                <div data-testid="devhub-doc-content" className="px-6 md:px-8 py-8">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{selected.content}</ReactMarkdown>
                </div>
              </motion.article>
            ) : (
              <div data-testid="devhub-empty" className="border border-dashed border-white/15 p-16 flex flex-col items-center gap-4 text-center">
                <BookOpen className="w-10 h-10 text-zinc-700" />
                <p className="font-mono text-xs text-zinc-500 max-w-sm leading-relaxed">
                  Select a document from the left, or write the first one. Good docs here follow the pattern: what it is, how to use it, common pitfalls.
                </p>
                <button data-testid="devhub-empty-new-button" onClick={() => setMode("new")} className="font-mono text-[10px] tracking-[0.25em] uppercase text-nc-blue border border-nc-blue/40 px-5 py-2.5 hover:bg-nc-blue/10 transition-colors flex items-center gap-2">
                  <Plus className="w-3.5 h-3.5" /> Write documentation
                </button>
              </div>
            )}
          </section>
        </main>
      )}

      {/* MOTD on entry */}
      <AnimatePresence>
        {!motdDismissed && <MotdModal announcements={announcements} onClose={() => setMotdDismissed(true)} />}
      </AnimatePresence>

      {/* History drawer */}
      <AnimatePresence>
        {historyFor && <HistoryPanel docId={historyFor} onClose={() => setHistoryFor(null)} onRestore={restoreVersion} />}
      </AnimatePresence>

      {/* Removal modal */}
      <AnimatePresence>
        {removalFor && (
          <motion.div data-testid="removal-modal" className="fixed inset-0 z-[95] bg-black/85 flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setRemovalFor(null)}>
            <div className="border border-white/15 bg-[#0a0a0a] p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              {isAdmin ? (
                <>
                  <h3 className="font-sans font-bold uppercase text-lg text-red-400 flex items-center gap-2"><Trash2 className="w-5 h-5" /> Permanent delete</h3>
                  <p className="font-mono text-xs text-zinc-400 mt-3 leading-relaxed">
                    This deletes the document and its entire version history. There is no undo.
                  </p>
                  <div className="flex gap-3 mt-6 justify-end">
                    <button data-testid="removal-cancel-button" onClick={() => setRemovalFor(null)} className="font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 border border-white/10 text-zinc-400">Cancel</button>
                    <button data-testid="removal-confirm-delete-button" onClick={() => adminDelete(removalFor)} className="bg-red-500 hover:bg-red-600 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-5 py-2.5 transition-colors">Delete forever</button>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="font-sans font-bold uppercase text-lg text-nc-orange flex items-center gap-2"><ShieldAlert className="w-5 h-5" /> Request removal</h3>
                  <p className="font-mono text-xs text-zinc-400 mt-3 leading-relaxed">
                    Devs can't delete documentation. Your request goes to an admin who reviews and deletes manually.
                  </p>
                  <textarea data-testid="removal-reason-input" rows={3} className={`${inputCls} mt-4 resize-none`} placeholder="Why should this doc be removed? (min 5 chars)" value={removalReason} onChange={(e) => setRemovalReason(e.target.value)} />
                  <div className="flex gap-3 mt-5 justify-end">
                    <button data-testid="removal-cancel-button" onClick={() => setRemovalFor(null)} className="font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 border border-white/10 text-zinc-400">Cancel</button>
                    <button data-testid="removal-submit-button" onClick={requestRemoval} disabled={removalReason.trim().length < 5} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-5 py-2.5 transition-colors">Submit request</button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <SiteFooter />
    </div>
  );
}
